import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { Divider, Grid, Stack, Typography } from '@mui/material';
import Button from '~/components/common/Button';
import { Field } from '~/components/common/Formik';
import { fromQueryString, toQueryString } from '~/utils/queryString';
import themes, { styled } from '~/themes';
import { debounce, find, flatten, isEqual, uniq } from 'lodash';
import { Icon } from '~/components/common/Icon';
import { Box } from '@mui/system';
import { fetchSuggestAddressBooks } from '../apis';

export const StyledFilterStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  rowGap: theme.spacing(2),
  columnGap: theme.spacing(0.8),
}));

interface AddressFilterFormProps {
  onSubmit?: (values: any) => void;
  onClear?: () => void;
  onClearAll?: () => void;
  stateCities: {
    city?: string[];
    state_info?: {
      state?: string;
      state_name?: string;
    };
  }[];
}

const defaultValues = {
  search: '',
  city: '',
  state: '',
  name: '',
  phone: '',
  email: '',
  address: '',
  postcode: '',
};

export const StyledForm = styled(Form)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

export const AddressFilterForm: React.FC<AddressFilterFormProps> = ({
  stateCities,
  onSubmit,
  onClear,
  onClearAll,
}) => {
  const formRef = useRef<any>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const { search, city, state, name, phone, email, address, postcode } =
    fromQueryString(location.search, { arrayFormat: 'none' });
  const [isExpand, setIsExpand] = useState(() => {
    if (phone || email || address || postcode || name) return true;
    return false;
  });

  const initialValues = {
    search: search || '',
    city: city || '',
    state: state || '',
    name: name || '',
    phone: phone || '',
    email: email || '',
    address: address || '',
    postcode: postcode || '',
  };

  const handleSearch = useCallback(
    debounce(() => {
      formRef.current.handleSubmit();
    }, 600),
    [],
  );

  const statesOptions = useMemo(
    () =>
      stateCities.map(({ state_info }) => ({
        label: state_info.state_name,
        value: state_info.state,
      })),
    [stateCities],
  );

  const renderState = useCallback(
    ({ setFieldValue, placeholder = 'Province', legend = '' }) => (
      <Field.Select
        {...(legend && { legend })}
        key='state'
        placeholder={placeholder}
        name='state'
        options={statesOptions}
        sortOption
        sxPlaceholder={{
          color: `${themes.color.grayPlaceHolder} !important`,
        }}
        onChange={() => {
          setFieldValue('city', '');
          if (!isExpand) formRef.current.handleSubmit();
        }}
        onClear={() => {
          setFieldValue('state', '');
          if (!isExpand) formRef.current.handleSubmit();
        }}
      />
    ),
    [statesOptions, isExpand],
  );

  const renderCity = useCallback(
    ({ values, setFieldValue, placeholder = 'City', legend = '' }) => {
      let citiesOptions = [];
      if (values.state) {
        const cities = stateCities.find(
          (e) => e.state_info?.state === values.state,
        )?.city;
        if (cities?.length)
          citiesOptions = cities.map((e) => ({
            label: e,
            value: e,
          }));
      } else {
        const cities = uniq(flatten(stateCities.map((item) => item.city)));
        citiesOptions = cities.map((e) => ({
          label: e,
          value: e,
        }));
      }

      return (
        <Field.Select
          {...(legend && { legend })}
          placeholder={placeholder}
          name='city'
          options={citiesOptions}
          sortOption
          sxPlaceholder={{
            color: `${themes.color.grayPlaceHolder} !important`,
          }}
          onChange={(e) => {
            const cityName = e.target.value as string;
            const matched = find(stateCities, (item) =>
              item.city.includes(cityName),
            );
            setFieldValue('state', matched?.state_info.state || '');
            if (!isExpand) formRef.current.handleSubmit();
          }}
          onClear={() => {
            setFieldValue('city', '');
            if (!isExpand) formRef.current.handleSubmit();
          }}
        />
      );
    },
    [stateCities, isExpand],
  );

  const AsyncSelectField = ({
    fieldName,
    legend,
    placeholder,
    fieldKey,
    setFieldValue,
  }) => (
    <Field.AsyncSelect
      legend={legend}
      name={fieldName}
      placeholder={placeholder}
      uniqueValue
      onBlur={(value) => {
        setTimeout(() => {
          setFieldValue(fieldName, value || '');
        }, 50);
      }}
      onClear={() => setFieldValue(fieldName, '')}
      fetchOptions={(debouncedSearchText) =>
        fetchSuggestAddressBooks({
          field: fieldName,
          search: debouncedSearchText,
        })
      }
      defaultOptions={[]}
      transformOptions={(data: any) => {
        if (!data?.length) return [];
        return data.map((item: any) => ({
          customLabel: item?.[fieldKey] || '',
          value: item?.id,
          inputValue: item?.[fieldKey],
          label: '',
          address: item,
        }));
      }}
      onChange={(_, addressBook) => {
        setFieldValue(fieldName, addressBook?.customLabel);
      }}
    />
  );

  const renderForm = ({ values, setFieldValue, resetForm, dirty }) => {
    if (isExpand)
      return (
        <StyledForm
          sx={{
            '& .MuiOutlinedInput-input': {
              '&::placeholder': {
                color: 'rgba(51, 54, 61, 0.40) !important',
              },
            },
            '& .MuiInputAdornment-root': {
              marginRight: '0px !important',
              marginLeft: '0px !important',
            },
            '& input': {
              paddingRight: '0 !important',
            },
          }}
        >
          <Grid
            container
            spacing={1.6}
            sx={{
              '.MuiAutocomplete-input': {
                paddingRight: '15px !important',
              },
            }}
          >
            <Grid item xs={6} md={3}>
              <AsyncSelectField
                fieldName='name'
                legend='Name'
                placeholder='Search'
                fieldKey='name'
                setFieldValue={setFieldValue}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <AsyncSelectField
                fieldName='phone'
                legend='Phone No.'
                placeholder='Search'
                fieldKey='phone'
                setFieldValue={setFieldValue}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <AsyncSelectField
                fieldName='email'
                legend='Email'
                placeholder='Search'
                fieldKey='email'
                setFieldValue={setFieldValue}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <AsyncSelectField
                fieldName='address'
                legend='Address'
                placeholder='Search'
                fieldKey='address_1'
                setFieldValue={setFieldValue}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              {renderState({
                setFieldValue,
                placeholder: 'Select option',
                legend: 'Province',
              })}
            </Grid>
            <Grid item xs={6} md={3}>
              {renderCity({
                values,
                setFieldValue,
                placeholder: 'Select option',
                legend: 'City',
              })}
            </Grid>
            <Grid item xs={6} md={3}>
              <AsyncSelectField
                fieldName='postcode'
                legend='Postal code'
                placeholder='Search'
                fieldKey='postcode'
                setFieldValue={setFieldValue}
              />
            </Grid>
          </Grid>
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.8,
              mt: 1.6,
            }}
          >
            <Button
              noRounder
              buttonType='primary-dark'
              sx={{
                minWidth: 'unset !important',
                padding: '4px 16px',
              }}
              onClick={() => formRef.current.handleSubmit()}
              disabled={isExpand && !dirty}
            >
              <Typography
                sx={{
                  minWidth: 'unset !important',
                  fontWeight: 500,
                }}
              >
                Apply
              </Typography>
            </Button>
            <Button
              buttonType='text'
              noRounder
              sx={{
                minWidth: 'unset !important',
                padding: '4px 16px',
                marginRight: '0px !important',
              }}
              onClick={() => {
                setIsExpand(false);
                resetForm();
                onClearAll();
              }}
            >
              <Typography
                sx={{
                  color: themes.color.violet500,
                  fontWeight: 500,
                }}
              >
                Cancel
              </Typography>
            </Button>
            <Divider orientation='vertical' flexItem />
            <Button
              buttonType='text'
              noRounder
              sx={{
                minWidth: 'unset !important',
                padding: '4px 16px',
                marginRight: '0px !important',
              }}
              onClick={() => {
                resetForm();
                onClearAll();
              }}
            >
              <Typography
                sx={{
                  color: themes.color.violet500,
                  fontWeight: 500,
                }}
              >
                Clear
              </Typography>
            </Button>
          </Stack>
        </StyledForm>
      );

    return (
      <StyledFilterStack>
        <Stack
          sx={{
            width: 300,
            [themes.breakpoints.down('md')]: {
              width: 'unset',
              flex: 1,
            },
          }}
        >
          <Field.SearchInput
            name='search'
            placeholder='Search'
            onClear={() => {
              onClear();
              setFieldValue('search', '');
            }}
            onChange={(e) => {
              setFieldValue('search', e.target.value);
              handleSearch();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                formRef.current.handleSubmit();
              }
            }}
          />
        </Stack>
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            rowGap: 2,
            columnGap: 0.8,
          }}
        >
          <Box
            sx={{
              width: 160,
              [themes.breakpoints.down('md')]: {
                display: 'none',
              },
            }}
          >
            {renderState({ setFieldValue, placeholder: 'Province' })}
          </Box>
          <Box
            sx={{
              width: 160,
              [themes.breakpoints.down('md')]: {
                display: 'none',
              },
            }}
          >
            {renderCity({ values, setFieldValue })}
          </Box>
          {!isEqual(values, defaultValues) && !isExpand && (
            <Button
              buttonType='text'
              onClick={() => {
                resetForm();
                onClearAll();
              }}
              sx={{
                '&.MuiButton-text': {
                  minWidth: 'unset !important',
                },
                [themes.breakpoints.down('md')]: {
                  display: 'none',
                },
              }}
            >
              <Typography
                sx={{
                  color: themes.color.violet500,
                  fontWeight: 500,
                }}
              >
                Clear
              </Typography>
            </Button>
          )}
          <Button
            buttonType='text'
            noRounder
            sx={{ minWidth: '35px !important' }}
            onClick={() => {
              navigate({
                pathname: location.pathname,
                search: toQueryString({ state, city }),
              });
              resetForm();
              setIsExpand(true);
            }}
          >
            <Icon
              name='filter'
              baseUrl='map'
              color='#33363D'
              useBackgroundImg
              size={16}
            />
          </Button>
        </Stack>
      </StyledFilterStack>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      innerRef={formRef}
    >
      {({ values, setFieldValue, resetForm, dirty }) =>
        renderForm({ values, setFieldValue, resetForm, dirty })
      }
    </Formik>
  );
};
